import React, { useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { useConfirm } from 'material-ui-confirm';
import { useQueryClient } from 'react-query';
import { translate as t } from 'hooks/useTranslations';
import { AppContext, storeLastUrl } from 'views/App/context';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import { Permission } from 'types/permissions_response';
import NewOrganization from '../New';
import StepUp from '../StepUp';
import useOrganizations from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganizations';
import useManageOrganization from 'views/App/Portal/LexID/Organizations/_data_sources/useManageOrganization';
import { Organization } from 'views/App/Portal/LexID/Organizations/_types/organization';
import { OrganizationsContext } from '../context';
import { Badge, Box, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import errorHelper from 'helpers/errorHelper';
import { hasStepUpAccess } from 'utils/stepUpHelper';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import Cookies from 'js-cookie';

const OrganizationsList: React.FC = () => {
  const currLoc = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { hasAppPermission, isFeatureEnabled, updatePermissions } = useContext(AppContext);
  const { params, updateParams } = useContext(OrganizationsContext);
  const [organizationsData, setOrganizationsData] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string } | null>(null);
  const [showStepUpModal, setShowStepUpModal] = useState(false);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { deleteOrganization } = useManageOrganization();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading, error } =
    useOrganizations<Organization>(params, {}, (errors) => {
      console.error('Error fetching organizations:', errors);
    });
  useEffect(() => {
    if (data && !isError) {
      const fetchedOrganizations = data.pages.flatMap((page) => page.content) || [];
      setOrganizationsData(fetchedOrganizations);
    }
  }, [data, isError]);
  const orgHasPermissions = useCallback((keys: string[], permissions: Permission[]) => {
    const permissionKeys = permissions.map((x) => x.action.toLowerCase());
    return keys.every((x) => permissionKeys.includes(x.toLowerCase()));
  }, []);

  useEffect(() => {
    if (!auth.user?.scopes?.includes('aws.cognito.signin.user.admin')) {
      // Check if we've attempted a redirect in the last 5 minutes
      const redirectAttempted = Cookies.get('phx-redirect-attempt');

      if (!redirectAttempted) {
        // Set cookie that expires in 5 minutes
        Cookies.set('phx-redirect-attempt', 'true', { expires: 5 / (24 * 60) }); // 5 minutes in days

        // Use the helper function to safely store the URL
        storeLastUrl(currLoc);

        console.log('Redirecting for additional scopes');
        auth.signinRedirect({
          scope: ['openid', 'profile', 'email', 'phone', 'aws.cognito.signin.user.admin'].join(' '),
        });
      } else {
        console.warn('Redirect already attempted, not redirecting again');
      }
    } else {
      // Delete the cookie if user has the required scope
      Cookies.remove('phx-redirect-attempt');
    }
  }, [auth.user?.scopes, currLoc, auth.signinRedirect]);

  const handleDeleteOrganization = useCallback(
    (orgId: string) => {
      confirm({
        description: t('organizations.actions.deleteConfirm'),
        confirmationText: t('organizations.actions.delete'),
        confirmationButtonProps: {
          variant: 'contained',
          color: 'error',
        },
      })
        .then(() => {
          deleteOrganization(
            orgId,
            () => {
              queryClient.invalidateQueries(['organizations']);
              enqueueSnackbar(t('organizations.actions.deleteSuccess'), {
                variant: 'success',
              });
            },
            (errors) => {
              errorHelper.enqueueErrors(errors);
            }
          );
        })
        .catch(() => {
          // don't do it
        });
    },
    [confirm, deleteOrganization, queryClient, t]
  );
  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('organizations.properties.name'),
        flex: 1.5,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const hasManagePermission = orgHasPermissions(
            ['initiateAuth', 'respondToChallenge'],
            row.authorizations || []
          );
          return (
            <span
              onClick={async () => {
                if (!hasManagePermission) {
                  return;
                }

                // Ensure org-level permissions from the row are persisted so that
                // downstream flows (e.g. switch-org) see accurate permissions.
                if (Array.isArray(row.authorizations) && row.authorizations.length > 0) {
                  updatePermissions(
                    row.id,
                    row.authorizations.map((auth: Permission) => auth.action)
                  );
                }

                if (hasStepUpAccess(row.id)) {
                  navigate(`/admin/switch-org/${row.id}?redir=${encodeURIComponent('/admin/lexid/settings')}`);
                } else {
                  setSelectedOrg({ id: row.id, name: row.name });
                  setShowStepUpModal(true);
                }
              }}
              style={{ cursor: 'pointer', color: '#004B87', fontWeight: 600 }}
            >
              {row.name}
              {row.organizationType === 'TEST' && (
                <Badge
                  variant='inline'
                  badgeContent='Test'
                  color='info'
                  sx={{ textTransform: 'uppercase', ml: 3 }}
                />
              )}
            </span>
          );
        },
      },
      location: {
        header: t('organizations.properties.location'),
        flex: 1,
      },
      userCount: {
        header: t('organizations.properties.userCount'),
        type: 'number',
        flex: 0.3,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.userCount}</span>;
        },
      },
      roleCount: {
        header: t('organizations.properties.roleCount'),
        type: 'number',
        flex: 0.3,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.roleCount}</span>;
        },
      },
      groupCount: {
        header: t('organizations.properties.groupCount'),
        type: 'number',
        flex: 0.3,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.groupCount}</span>;
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const items = [
            ...(orgHasPermissions(['initiateAuth', 'respondToChallenge'], row.authorizations || [])
              ? [
                  {
                    key: `manage_org_${row.id}`,
                    label: t('organizations.actions.manageOrganization'),
                    onClick: () => {
                      if (Array.isArray(row.authorizations) && row.authorizations.length > 0) {
                        updatePermissions(
                          row.id,
                          row.authorizations.map((auth: Permission) => auth.action)
                        );
                      }

                      if (hasStepUpAccess(row.id)) {
                        navigate(`/admin/switch-org/${row.id}?redir=${encodeURIComponent('/admin/lexid/settings')}`);
                      } else {
                        setSelectedOrg({ id: row.id, name: row.name });
                        setShowStepUpModal(true);
                      }
                    },
                  },
                ]
              : []),
            ...(orgHasPermissions(['editOrgSolution'], row.authorizations || [])
              ? [
                  {
                    key: `manage_solutions_${row.id}`,
                    label: t('organizations.actions.manageSolutions'),
                    onClick: () => {
                      if (Array.isArray(row.authorizations) && row.authorizations.length > 0) {
                        updatePermissions(
                          row.id,
                          row.authorizations.map((auth: Permission) => auth.action)
                        );
                      }

                      navigate(
                        `/admin/switch-org/${row.id}?redir=${encodeURIComponent('/admin/lexid/manage-solutions')}`
                      );
                    },
                  },
                ]
              : []),
            ...(row.userCount === 0 && orgHasPermissions(['deleteOrg'], row.authorizations || [])
              ? [
                  {
                    key: `delete_org_${row.id}`,
                    label: t('organizations.actions.delete'),
                    onClick: () => handleDeleteOrganization(row.id),
                  },
                ]
              : []),
          ];
          return (
            <ActionMenu
              id={`action_menu_${row.id}`}
              items={items}
            />
          );
        },
      },
    }),
    [t, navigate, orgHasPermissions, handleDeleteOrganization]
  );
  const actions = useMemo(() => (hasAppPermission('createOrg') ? <NewOrganization /> : null), [hasAppPermission]);
  const transformedData = useMemo(
    () =>
      organizationsData.map((o) => ({
        ...o.organization,
        cognito: o.cognitoPool,
        location: `${o.organization.city}, ${o.organization.state}`,
      })),
    [organizationsData]
  );
  const gridViewQuery = useMemo(
    () => ({
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      isError,
      isLoading,
      error,
      data: {
        pages:
          data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [hasNextPage, isFetchingNextPage, fetchNextPage, isError, isLoading, error, data]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('organizations.title'),
        actions: actions,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <QuickSearch
              id='org_keyword_search'
              initialValue={params.search || ''}
              onChange={(val) => updateParams({ search: val })}
              placeholder={t('organizations.search.placeholder')}
            />
          }
        />
        <GridSection
          columns={columns}
          data={transformedData}
          query={gridViewQuery}
        />
      </Box>
      {showStepUpModal && selectedOrg && (
        <StepUp
          open={showStepUpModal}
          onClose={() => {
            setShowStepUpModal(false);
            setSelectedOrg(null);
          }}
          organizationId={selectedOrg.id}
          organizationName={selectedOrg.name}
        />
      )}
    </CommonLayout>
  );
};

export default OrganizationsList;
